import React from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import UserInitials, { UnassignedIcon } from "components/comments/UserInitials";
import UserName, { formatUserNameWithStatus } from "components/users/UserName";
import { useCompany } from "lib/hooks/api/company/useCompany";
import { Select, SelectProps } from "./Inputs";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options">;

type Option = {
  label: string;
  value: string;
  renderOption: React.ReactNode;
  renderSelectedOption: React.ReactNode;
};

function CompanyUserOption({
  id,
  icon,
  text,
}: {
  id: string;
  icon: React.ReactNode;
  text: React.ReactNode;
}) {
  return (
    <CompanyUserSelectOption key={id}>
      {icon}
      <Text>{text}</Text>
    </CompanyUserSelectOption>
  );
}

export default function CompanyUserSelect<T extends FieldValues>(props: FieldProps<T>) {
  const { field } = useController(props);

  const { data: company, isLoading: isLoadingCompany } = useCompany({ includeInactive: true });

  const currentValue = field.value;
  // Assignment picker — hide deactivated users from the options list, BUT keep the
  // currently-selected user even if they've since been deactivated, so the picker
  // can still display the previously-assigned value. The (deactivated) suffix only
  // renders on that one preserved option.
  const visibleUsers =
    company?.users.filter((user) => user.active !== false || user.guid === currentValue) ?? [];

  const options: Option[] = visibleUsers.map((user) => ({
    value: user.guid,
    label: formatUserNameWithStatus(user),
    renderOption: (
      <CompanyUserOption
        id={user.guid}
        text={<UserName user={user} />}
        icon={
          <UserInitials
            firstName={user?.firstName}
            lastName={user?.lastName}
            showTooltip={false}
            type="extra-small"
          />
        }
      />
    ),
    renderSelectedOption: (
      <Flex alignItems="center" gap={8}>
        <UserInitials
          firstName={user?.firstName}
          lastName={user?.lastName}
          showTooltip={false}
          type="extra-small"
        />
        <Text>
          <UserName user={user} />
        </Text>
      </Flex>
    ),
  }));

  const unassignedOption = {
    label: "Unassigned",
    value: "" as unknown as null,
    renderOption: (
      <CompanyUserOption
        id="unassigned"
        text="Unassigned"
        icon={<UnassignedIcon showTooltip={false} type="extra-small" />}
      />
    ),
    renderSelectedOption: (
      <Flex alignItems="center" gap={8}>
        <UnassignedIcon showTooltip={false} type="extra-small" />
        <Text>Unassigned</Text>
      </Flex>
    ),
  };

  const allOptions = [unassignedOption, ...options];

  return (
    <Select
      {...props}
      placeholder="Select user"
      options={allOptions}
      value={field.value || ""}
      // Can put this prop in since we handle field.onChange in this component,
      // rather than in the form component - this allows us to transform the
      // value (e.g., "" to null) before passing it to the form
      preventFormValueChange
      onChange={(val) => {
        field.onChange(val || null);
        props.onChange?.(val || null);
      }}
      onClear={() => field.onChange(null)}
      loading={isLoadingCompany}
      optionRender={({ data }) => data.renderOption}
      optionLabelProp={"renderSelectedOption"}
    />
  );
}

const CompanyUserSelectOption = styled(Flex)({
  alignItems: "center",
  gap: 8,
  padding: "8px 0",
});
